import React, { Component } from "react";
import axios from "axios";
import { Link, Navigate } from "react-router-dom";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import { apiErrorMessage } from "Helpers/ContentValidation";

import DataTable from "examples/Tables/DataTable";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { Card, Chip, Grid, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDTypography from "components/MDTypography";

/**
 * Editable website pages — CSR, Compliance, Partners, Investor Relations, the
 * All Products hero and any page added later.
 *
 * Each page is a list of content blocks, so a new initiative or certification
 * is added here rather than in code.
 */
export default class SitePages extends Component {
  state = {
    pages: [],
    loading: false,
    notification: false,
    notiColor: "success",
    message: "",
    redirect: false,
  };

  componentDidMount() {
    this.fetchPages();
  }

  fetchPages = async () => {
    if (init() !== "success") return this.setState({ redirect: true });

    this.setState({ loading: true });

    try {
      const response = await axios.get(`${Constants.getUrls.getAllSitePages}?limit=200`);
      this.setState({ pages: response.data?.data?.docs || [], loading: false });
    } catch (error) {
      this.setState({ loading: false });
      this.notify("warning", apiErrorMessage(error, "Could not load pages"));
    }
  };

  notify = (notiColor, message) => {
    this.setState({ notification: true, notiColor, message });
    setTimeout(() => this.setState({ notification: false, message: "" }), 4000);
  };

  handleToggleStatus = async (page) => {
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.patch(
        Constants.patchUrls.toggleSitePageStatus + page._id
      );
      this.notify("success", response.data?.message || "Status updated");
      this.fetchPages();
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not change the status"));
    }
  };

  handleDelete = async (page) => {
    if (
      !window.confirm(
        `Delete the page content for "${page.name}"? The website will fall back to whatever it renders without it.`
      )
    ) {
      return;
    }
    if (init() !== "success") return this.setState({ redirect: true });

    try {
      const response = await axios.delete(Constants.deleteUrls.deleteSitePage + page._id);
      this.notify("success", response.data?.message || "Page deleted");
      this.fetchPages();
    } catch (error) {
      this.notify("warning", apiErrorMessage(error, "Could not delete the page"));
    }
  };

  render() {
    const { pages, loading, redirect } = this.state;

    if (redirect) return <Navigate to="/signin" replace />;

    return (
      <DashboardLayout>
        <DashboardNavbar />

        <MDBox pt={6} pb={3}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Website Pages
              </MDTypography>
              <MDTypography variant="caption" color="white">
                Headings, text blocks, icon lists and logo grids for CSR, Compliance,
                Partners and the other content pages
              </MDTypography>
            </MDBox>

            <Grid item sm={4} px={2} pt={2} align="right">
              <Link to="/addsitepage">
                <MDButton variant="contained" size="small" color="primary">
                  Add Page
                </MDButton>
              </Link>
            </Grid>

            <MDBox py={3}>
              {loading ? (
                <Grid align="center">
                  <h4>Loading...</h4>
                </Grid>
              ) : pages.length > 0 ? (
                <DataTable
                  canSearch
                  entriesPerPage={{ defaultValue: 20, entries: [10, 20, 50] }}
                  table={{
                    columns: [
                      { Header: "S.No", accessor: "serial", width: "6%" },
                      { Header: "Page", accessor: "page" },
                      { Header: "Sections", accessor: "sections" },
                      { Header: "Status", accessor: "status" },
                      { Header: "Actions", accessor: "action" },
                    ],
                    rows: pages.map((page, index) => {
                      const sections = page.sections || [];
                      const hidden = sections.filter(
                        (section) => section.status === "inactive"
                      ).length;

                      return {
                        serial: index + 1,

                        page: (
                          <MDBox>
                            <MDTypography
                              variant="button"
                              fontWeight="medium"
                              display="block"
                            >
                              {page.name}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              {page.page_route || `/${page.slug}`}
                            </MDTypography>
                          </MDBox>
                        ),

                        sections: (
                          <MDTypography variant="caption">
                            {sections.length}
                            {hidden ? ` (${hidden} hidden)` : ""}
                          </MDTypography>
                        ),

                        status: (
                          <Chip
                            size="small"
                            label={page.status === "active" ? "Active" : "Inactive"}
                            color={page.status === "active" ? "success" : "default"}
                          />
                        ),

                        action: (
                          <div style={{ display: "flex", gap: 6 }}>
                            <Tooltip title="Edit page content">
                              <Link to={`/updatesitepage/${page._id}`}>
                                <IconButton size="small" color="info">
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Link>
                            </Tooltip>

                            <Tooltip
                              title={
                                page.status === "active"
                                  ? "Hide from the website"
                                  : "Show on the website"
                              }
                            >
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => this.handleToggleStatus(page)}
                              >
                                {page.status === "active" ? (
                                  <VisibilityIcon fontSize="small" />
                                ) : (
                                  <VisibilityOffIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Delete page content">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => this.handleDelete(page)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        ),
                      };
                    }),
                  }}
                />
              ) : (
                <div style={{ padding: 20 }}>No Pages Found!</div>
              )}
            </MDBox>

            <MDSnackbar
              color={this.state.notiColor}
              icon="notifications"
              title="Website Pages"
              content={this.state.message}
              dateTime="Just now"
              open={this.state.notification}
              close={() => this.setState({ notification: false })}
            />
          </Card>
        </MDBox>
      </DashboardLayout>
    );
  }
}
